export const API_BASE = '/v1';

/** An RFC 9457 `application/problem+json` body, as every API error returns. */
export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: { path: string; message: string }[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The parsed problem document, when the server sent one. */
    public problem?: Problem,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Turn an error response into something worth showing a user.
 *
 * Every API failure is `application/problem+json` (see
 * `apps/api/src/plugins/error-handler.ts`), so using the raw body as the
 * message — as this used to — put the entire JSON document verbatim into
 * toasts like "Autosave failed", `type` URI and all. Field-level validation
 * errors are appended because the `title` on its own ("Request validation
 * failed") never says which field is at fault.
 */
export async function describeFailure(response: {
  status: number;
  statusText: string;
  text: () => Promise<string>;
}): Promise<{ message: string; problem?: Problem }> {
  const body = await response.text();
  if (body.trim() === '') return { message: `${response.status} ${response.statusText}`.trim() };

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { message: body };
  }
  if (parsed === null || typeof parsed !== 'object') return { message: body };

  const problem = parsed as Problem;
  const headline = problem.detail ?? problem.title;
  if (typeof headline !== 'string' || headline === '') return { message: body, problem };

  const fields = (problem.errors ?? [])
    .map((error) => `${error.path ?? ''} ${error.message ?? ''}`.trim())
    .filter((field) => field !== '');

  return { message: fields.length > 0 ? `${headline} (${fields.join('; ')})` : headline, problem };
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when there is one. Sending
  // `Content-Type: application/json` on a bodiless request makes Fastify's
  // content-type parser reject it outright — "Body cannot be empty when
  // content-type is set to 'application/json'" — which meant every DELETE
  // this client issued came back 400, so deleting a mission from the explorer
  // never actually worked.
  const headers: Record<string, string> = {
    ...(init?.body === undefined || init.body === null ? {} : { 'Content-Type': 'application/json' }),
    ...(init?.headers as Record<string, string> | undefined),
  };

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (!response.ok) {
    const { message, problem } = await describeFailure(response);
    throw new ApiError(response.status, message, problem);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}
